<template>
  <card v-if="user" :title="$t('pages.profile.sections.twoFactor.title')">
    <p>{{ $t("pages.profile.sections.twoFactor.description") }}</p>
    <p class="mt-11" v-html="activatedMessage" />
    <div class="flex gap-x-4">
      <custom-button
        v-if="!user.twoFactorEnabled"
        variant="secondary"
        class="mt-4"
        @click="openModal"
      >
        {{ $t("pages.profile.sections.twoFactor.activateButtonText") }}
      </custom-button>
      <custom-button
        v-else
        variant="primary"
        class="mt-4"
        :busy="loadingDisable"
        @click="disableTwoFactor"
      >
        {{ $t("pages.profile.sections.twoFactor.deactivateButtonText") }}
      </custom-button>
    </div>

    <modal v-model:open="enableModalOpen">
      <template #header>
        <button class="absolute z-10 top-10 right-10 w-5 h-5" @click="closeModal">
          <close />
        </button>
      </template>

      <template #default>
        <article class="text-center my-37.5 px-12.5">
          <header class="border-b border-b-element-neutral pb-5">
            <h2 class="whitespace-nowrap">
              {{ $t("pages.profile.sections.twoFactor.modal.title") }}
            </h2>
            <p class="mt-2.5">
              {{ $t("pages.profile.sections.twoFactor.modal.description") }}
            </p>
          </header>
          <form class="pt-18.5" @submit.prevent="enableTwoFactor">
            <p class="font-bold">
              {{ $t("pages.profile.sections.twoFactor.modal.step1") }}
            </p>
            <busy-area :busy="loadingQr">
              <canvas ref="canvas" class="m-auto w-50 h-50" />
            </busy-area>
            <p class="font-bold mt-14">
              {{ $t("pages.profile.sections.twoFactor.modal.step2") }}
            </p>
            <InputField
              v-model="code"
              class="mt-2.5"
              placeholder="565662"
              :pattern="/[0-9]+/"
              :error="errors"
            />
            <footer class="mt-17.5 border-t border-t-element-neutral">
              <p class="mt-8">
                {{ $t("pages.profile.sections.twoFactor.modal.done") }}
              </p>
              <custom-button
                variant="primary"
                class="mt-7.5 w-full"
                :busy="loadingEnable"
                type="submit"
              >
                {{ $t("pages.profile.sections.twoFactor.modal.closeButton") }}
              </custom-button>
            </footer>
          </form>
        </article>
      </template>
    </modal>
  </card>
</template>

<script lang="ts" setup>
import Card from '@components/utils/Card.vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '@components/utils/Modal/Modal.vue';
import InputField from '@components/utils/inputs/Input.vue';
import Close from '@images/close.svg?component';
import QRCode from 'qrcode';
import { useValidation } from '@hooks/form';
import { string } from 'yup';
import CustomButton from '@components/utils/CustomButton.vue';
import BusyArea from '@components/utils/busy/BusyArea.vue';
import { useStore } from '@/store';
import { FetchError, useFetchAction } from '@/hooks/fetch';
import { useNotify } from '@/components/utils/notifications';
import { syncRef } from '@/hooks/state';

const TWO_FACTOR_ERROR_CODE = 'wrong_two_factor_code';

const { t } = useI18n();
const store = useStore();
const enableModalOpen = ref(false);
const user = syncRef(
  computed(() => store.state.auth.user),
  (u) => ({ ...u }),
);

const activatedMessage = computed(() => t('pages.profile.sections.twoFactor.activated', {
  active: user.value.twoFactorEnabled ? t('activated') : t('deactivated'),
}));

const canvas = ref<HTMLCanvasElement | null>(null);
// const code = useGuardedRef(ref(""), (value) => value.replaceAll(/[^\d]/g, "") + "");
const code = ref('');

const notify = useNotify();

const { errors, validatedFn } = useValidation(
  string()
    .required(t('pages.profile.sections.twoFactor.modal.codeRequired'))
    .matches(/\d+/, t('pages.profile.sections.twoFactor.modal.codeNoDot'))
    .min(6, t('pages.profile.sections.twoFactor.modal.codeLength', { length: 6 }))
    .max(6, t('pages.profile.sections.twoFactor.modal.codeLength', { length: 6 })),
  code,
);
const [query, { loading: loadingQr }] = useFetchAction();
const [enable, { loading: loadingEnable }] = useFetchAction();
const [disable, { loading: loadingDisable }] = useFetchAction();

const requestCode = async () => {
  const { qrCode } = (await query('/users/setup-2fa', {
    method: 'POST',
  })) as { qrCode: string };

  QRCode.toCanvas(canvas.value, qrCode, { width: 200 });
};

const openModal = () => {
  enableModalOpen.value = true;
  requestCode();
};

const closeModal = () => {
  enableModalOpen.value = false;
};

const disableTwoFactor = async () => {
  try {
    await disable('/users/disable-2fa', {
      method: 'DELETE',
    });
    store.dispatch('auth/LOAD_USER');
    notify?.({
      title: t('pages.profile.sections.twoFactor.successDisableNotification'),
      variant: 'success',
    });
  } catch (e) {
    notify?.({
      title: t('serverError'),
      variant: 'error',
    });
  }
};

const enableTwoFactor = validatedFn(async () => {
  try {
    await enable('/users/confirm-2fa', {
      method: 'POST',
      body: {
        twoFactorCode: code.value,
      },
    });
    store.dispatch('auth/LOAD_USER');
    closeModal();
    notify?.({
      title: t('pages.profile.sections.twoFactor.modal.successNotification'),
      variant: 'success',
    });
  } catch (e) {
    if (e instanceof FetchError) {
      const errorCode = await e.response.json();
      if (errorCode?.error?.key === TWO_FACTOR_ERROR_CODE) {
        notify?.({
          title: t('pages.profile.sections.twoFactor.modal.errorNotification'),
          variant: 'error',
        });

        return;
      }
    }

    notify?.({
      title: t('serverError'),
      variant: 'error',
    });
  }
});
</script>

<style lang="scss" scoped></style>
